import type { Content } from '@tiptap/react';
import * as React from 'react';
import { LuLoader } from 'react-icons/lu';

import {
    GlobalAccount,
    GUID,
    TaskStatus,
    TaskTimelineEntryType,
    TaskTimelineEntryView,
    TaskView,
    Tenant,
} from '@ndvr/customer-api';

import { ConfirmModal } from '@/components/ConfirmModal';
import { MinimalTiptapEditor, MinimalTiptapEditorNoToolbar } from '@/components/minimal-tiptap';
import { MentionSuggestionItem } from '@/components/minimal-tiptap/extensions';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { EllipsisDropdownMenuTrigger } from '@/components/ui/dropdown-menu-triggers';
import { convertPlainTextToHtml, formatDateTime, hasVisibleTextContent, titleCase } from 'utils';
import { type TenantTeamNameResolver, useRecordTeamNameResolver } from 'views/admin/tenantTeam/tenant-team-queries';
import { Toast } from 'widgets';

import { useDeleteTaskComment, useTaskTimeline, useUpdateTaskComment } from '../task-queries';
import { extractMentionsFromHtml } from '../task-utils';

const getEditorStringValue = (value: Content) => (typeof value === 'string' ? value : '');

export interface TaskActivitySectionProps {
    taskGuid: GUID<TaskView>;
    /** Tenant whose team names the timeline actors; the household's tenant is used when absent. */
    tenantGUID?: GUID<Tenant> | null;
    householdGUID?: GUID<GlobalAccount> | null;
    currentPersonOrgGUID?: string;
    mentionSuggestions: MentionSuggestionItem[];
}

export const TaskActivitySection = ({
    taskGuid,
    tenantGUID,
    householdGUID,
    currentPersonOrgGUID,
    mentionSuggestions,
}: TaskActivitySectionProps) => {
    const { data: timeline = [], isLoading } = useTaskTimeline(taskGuid);
    const updateTaskComment = useUpdateTaskComment();
    const deleteTaskComment = useDeleteTaskComment();
    const { nameFor } = useRecordTeamNameResolver({ tenantGUID, householdGUID });

    const [editingCommentGUID, setEditingCommentGUID] = React.useState<string | null>(null);
    const [editingContent, setEditingContent] = React.useState('');
    const [commentGUIDPendingDelete, setCommentGUIDPendingDelete] = React.useState<string | null>(null);

    const handleSaveEdit = async () => {
        if (!editingCommentGUID || !hasVisibleTextContent(editingContent)) return;
        try {
            await updateTaskComment.mutateAsync({
                taskGuid,
                commentGUID: editingCommentGUID,
                request: {
                    content: editingContent,
                    mentions: extractMentionsFromHtml(editingContent),
                },
            });
            setEditingCommentGUID(null);
            setEditingContent('');
        } catch (error) {
            Toast.forError(error as Error, 'Failed to update comment');
        }
    };

    const handleDelete = async (commentGUID: string) => {
        try {
            await deleteTaskComment.mutateAsync({ taskGuid, commentGUID });
            if (editingCommentGUID === commentGUID) {
                setEditingCommentGUID(null);
                setEditingContent('');
            }
        } catch (error) {
            Toast.forError(error as Error, 'Failed to delete comment');
        }
    };

    return (
        <>
            <div className="space-y-3">
                <div className="space-y-1">
                    <h3 className="text-sm font-medium">Activity</h3>
                    <p className="text-muted-foreground text-xs">Newest activity appears first.</p>
                </div>
                {isLoading ? (
                    <div className="text-muted-foreground flex items-center gap-2 text-sm">
                        <LuLoader className="h-4 w-4 animate-spin" />
                        <span>Loading timeline…</span>
                    </div>
                ) : timeline.length === 0 ? (
                    <div className="text-muted-foreground rounded-md border border-dashed p-4 text-sm">
                        No activity yet.
                    </div>
                ) : (
                    <div className="space-y-3">
                        {timeline.map((entry, index) => (
                            <TaskTimelineEntryCard
                                key={`${entry.timelineEntryType}-${entry.commentGUID ?? entry.timestamp}-${index}`}
                                entry={entry}
                                actorName={getTimelineActorName(entry, nameFor)}
                                isEditing={editingCommentGUID === entry.commentGUID}
                                isOwnComment={
                                    entry.timelineEntryType === TaskTimelineEntryType.COMMENT &&
                                    entry.actorPersonOrgGUID === currentPersonOrgGUID
                                }
                                editingContent={editingContent}
                                isUpdatePending={updateTaskComment.isPending}
                                mentions={mentionSuggestions}
                                onEdit={() => {
                                    setEditingCommentGUID(entry.commentGUID ?? null);
                                    setEditingContent(entry.content ?? '');
                                }}
                                onCancelEdit={() => {
                                    setEditingCommentGUID(null);
                                    setEditingContent('');
                                }}
                                onDelete={() => entry.commentGUID && setCommentGUIDPendingDelete(entry.commentGUID)}
                                onEditingContentChange={setEditingContent}
                                onSaveEdit={handleSaveEdit}
                            />
                        ))}
                    </div>
                )}
            </div>
            <ConfirmModal
                open={Boolean(commentGUIDPendingDelete)}
                onOpenChange={(open) => {
                    if (!open) setCommentGUIDPendingDelete(null);
                }}
                title="Delete Comment"
                description="Are you sure you want to delete this comment? This action cannot be undone."
                confirmText="Delete"
                confirmVariant="destructive"
                loading={deleteTaskComment.isPending}
                onCancel={() => setCommentGUIDPendingDelete(null)}
                onConfirm={() => {
                    if (!commentGUIDPendingDelete) return;
                    void handleDelete(commentGUIDPendingDelete);
                }}
            />
        </>
    );
};

// ── Supporting components ────────────────────────────────────────────────────

const TaskTimelineEntryCard = ({
    entry,
    actorName,
    isEditing,
    isOwnComment,
    editingContent,
    isUpdatePending,
    mentions,
    onEdit,
    onCancelEdit,
    onDelete,
    onEditingContentChange,
    onSaveEdit,
}: {
    entry: TaskTimelineEntryView;
    actorName: string;
    isEditing: boolean;
    isOwnComment: boolean;
    editingContent: string;
    isUpdatePending: boolean;
    mentions: MentionSuggestionItem[];
    onEdit: () => void;
    onCancelEdit: () => void;
    onDelete: () => void;
    onEditingContentChange: (value: string) => void;
    onSaveEdit: () => void;
}) => {
    const isComment = entry.timelineEntryType === TaskTimelineEntryType.COMMENT;

    return (
        <div className="space-y-3 rounded-md border p-3">
            <div className="flex items-start justify-between gap-3">
                <div className="space-y-1">
                    <p className="text-sm font-medium">{actorName}</p>
                    <div className="text-muted-foreground flex flex-wrap items-center gap-2 text-xs">
                        <span>{formatDateTime(entry.timestamp)}</span>
                        {isComment && entry.editedAt && <span>(edited {formatDateTime(entry.editedAt)})</span>}
                    </div>
                </div>
                {isOwnComment && entry.commentGUID && !isEditing && (
                    <DropdownMenu>
                        <EllipsisDropdownMenuTrigger />
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={onEdit}>Edit</DropdownMenuItem>
                            <DropdownMenuItem onClick={onDelete} className="text-red-600 focus:text-red-600">
                                Delete
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                )}
            </div>
            {isComment ? (
                <TaskCommentBody
                    isEditing={isEditing}
                    content={entry.content}
                    editingContent={editingContent}
                    isUpdatePending={isUpdatePending}
                    mentions={mentions}
                    onCancelEdit={onCancelEdit}
                    onEditingContentChange={onEditingContentChange}
                    onSaveEdit={onSaveEdit}
                />
            ) : (
                <p className="text-sm">{getTimelineEventCopy(entry)}</p>
            )}
        </div>
    );
};

const TaskCommentBody = ({
    isEditing,
    content,
    editingContent,
    isUpdatePending,
    mentions,
    onCancelEdit,
    onEditingContentChange,
    onSaveEdit,
}: {
    isEditing: boolean;
    content?: string;
    editingContent: string;
    isUpdatePending: boolean;
    mentions: MentionSuggestionItem[];
    onCancelEdit: () => void;
    onEditingContentChange: (value: string) => void;
    onSaveEdit: () => void;
}) => {
    if (!isEditing) {
        return (
            <MinimalTiptapEditorNoToolbar
                key={content || 'empty-comment'}
                mentions={mentions}
                value={convertPlainTextToHtml(content || '')}
                className="w-full border-0 text-sm shadow-none"
                output="html"
                editable={false}
                editorClassName="focus:outline-hidden"
            />
        );
    }

    return (
        <div className="space-y-3">
            <MinimalTiptapEditor
                mentions={mentions}
                value={convertPlainTextToHtml(editingContent)}
                onChange={(nextValue) => onEditingContentChange(getEditorStringValue(nextValue))}
                className="w-full"
                editorContentClassName="p-5"
                output="html"
                editable={!isUpdatePending}
                editorClassName="focus:outline-hidden"
            />
            <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={onCancelEdit} disabled={isUpdatePending}>
                    Cancel
                </Button>
                <Button
                    size="sm"
                    onClick={onSaveEdit}
                    disabled={!hasVisibleTextContent(editingContent) || isUpdatePending}
                >
                    {isUpdatePending ? 'Saving…' : 'Save'}
                </Button>
            </div>
        </div>
    );
};

// ── Helpers ──────────────────────────────────────────────────────────────────

const getTimelineActorName = (entry: TaskTimelineEntryView, nameFor: TenantTeamNameResolver) => {
    if (!entry.actorPersonOrgGUID) return 'System';
    return nameFor(entry.actorPersonOrgGUID) ?? 'Unknown User';
};

const getTimelineEventCopy = (entry: TaskTimelineEntryView) => {
    switch (entry.timelineEntryType) {
        case TaskTimelineEntryType.TASK_CREATED:
            return 'created the task';
        case TaskTimelineEntryType.TASK_STATUS_CHANGED:
            return `changed status from ${formatTaskStatus(entry.statusFrom)} to ${formatTaskStatus(entry.statusTo)}`;
        case TaskTimelineEntryType.TASK_UPDATED:
            return 'updated task details';
        default:
            return '';
    }
};

const formatTaskStatus = (status?: TaskStatus) => (status ? titleCase(status) : 'Unknown');
